import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";

import { setStep, updateOrganization } from "../modules/actions";
import { communityTypes, posBrands, communitySizeOptions } from "../config";

import PosIcon from "../assets/pos.svg";
import BusinessIcon from "../assets/business.svg";
import ClubsIcon from "../assets/community.svg";
import WeddingIcon from "../assets/wedding.svg";
import FireIcon from "../assets/fw.svg";

import DropDown from "./DropDown";
import TextInput from "./TextInput";

const organizationTypes = [
  {
    title: "Verein / Gemeinschaft",
    type: "club",
    question: "Dein Verein / Deine Gemeinschaft?",
    icon: ClubsIcon,
  },
  {
    title: "Feuerwehr",
    type: "fire",
    question: "Deine Feuerwehr?",
    icon: FireIcon,
  },
  {
    title: "Supermarkt",
    type: "pos",
    question: "Ihr Markt?",
    icon: PosIcon,
  },
  {
    title: "Unternehmen",
    type: "business",
    question: "Ihr Unternehmen?",
    icon: BusinessIcon,
  },
  {
    title: "Hochzeit",
    type: "wedding",
    question: "Eure Hochzeit?",
    icon: WeddingIcon,
  },
];

const designerPrompts = {
  business: {
    text: "Alle Informationen zum Sammelerlebnis für Unternehmen findest du unter business.stickerstars.de",
    url: "https://business.stickerstars.de",
  },
  wedding: {
    text: "Alle Informationen zum Sammelerlebnis für Hochzeiten findest du unter wedding.stickerstars.de",
    url: "https://wedding.stickerstars.de",
  },
};

const SubTypeSizeInfo = ({ other_type, type }) => {
  const data = {
    sport: () => (
      <div>
        In einem Stickeralbum bilden wir immer Deinen <b>gesamten Verein</b> ab.
        <br />
        <br />
        Um abschätzen zu können, wie viele aktive Personen Euer Verein umfasst,
        kannst du folgende <b>"Faustformel"</b> nutzen:
        <br />
        <ul>
          <li>
            Fußball oder Hockey:
            <br />
            20 x Anzahl Teams
          </li>
          <li>
            Handball oder Basketball:
            <br />
            15 x Anzahl Teams
          </li>
        </ul>
      </div>
    ),
    fire: () => (
      <div>
        In einem Stickeralbum bilden wir gerne die <b>gesamte Feuerwehr</b> ab.
        <br />
        <br />
        Schätze also gerne ab, auf wie viele Personen ihr im Album kommt, wenn
        neben Deiner Ortsfeuerwehr auch die die anderen Ortsfeuerwehren Eurer
        Gemeinde mit einbezogen werden.
        <br />
        <br />
        Falls du das nicht genau abschätzen kann - kein Problem:
        <br />
        Schätze einfach nach Gefühl!
      </div>
    ),
    default: () => (
      <div>
        In einem Stickeralbum bilden wir gerne die <b>gesamte Gemeinschaft</b>{" "}
        ab
        <br />
        Schätze also ab, wieviele Personen zusammenkommen, wenn{" "}
        <b>alle Mitglieder</b> Teil des Albums werden.
      </div>
    ),
  };

  return data[type]?.() || data[other_type]?.() || data.default() || null;
};

const Organization = ({ organization, dispatch }) => {
  const titleRef = useRef(null);

  useEffect(() => {
    if (titleRef && titleRef.current) {
      titleRef.current.focus();
    }
  }, []);

  const invalidSize =
    ["club", "fire", "community"].includes(organization.type) &&
    organization.size === "";

  const invalid = organization.name === "" || invalidSize;

  const handleSubmit = (e) => {
    if (invalid) {
      return;
    }

    e.preventDefault();
    dispatch(setStep(1));

    window.analytics?.track("Lead Form Organization Step Submitted");
  };

  const currentOrg = organizationTypes.find(
    ({ type }) => type === organization.type
  );

  const handleOrganizationClick = type => {
    window.analytics?.track("Lead Form Organization Type Selected", { type });
    dispatch(updateOrganization("type", type))
  }

  return (
    <form onSubmit={handleSubmit}>
      <span className="question">Was beschreibt Euch am besten?</span>
      <div className="organizations-container">
        {organizationTypes.map(({ title, type, icon }) => (
          <div
            key={type}
            className={`organization-badge ${
              organization.type === type ? "active" : ""
            }`}
            onClick={() => handleOrganizationClick(type)}
          >
            <img src={icon} alt="organization icon" />
            <span className="title">{title}</span>
          </div>
        ))}
      </div>
      {["club", "fire", "pos"].includes(organization.type) && (
        <TextInput
          label={`Wie heißt ${currentOrg && currentOrg.question}`}
          value={organization.name}
          onChange={({ target: { value } }) =>
            dispatch(updateOrganization("name", value))
          }
          valid={organization.name.length > 0}
          ref={titleRef}
        />
      )}
      {["club"].includes(organization.type) && (
        <>
          <DropDown
            label="Welche Art Gemeinschaft seid Ihr?"
            options={communityTypes}
            value={organization.other_type}
            onChange={({ target: { value } }) =>
              dispatch(updateOrganization("other_type", value))
            }
            includeBlank
          />
        </>
      )}
      {["club", "fire"].includes(organization.type) && (
        <DropDown
          label="Wie viele Personen sollen in Euer Album?"
          options={communitySizeOptions}
          value={organization.size}
          onChange={({ target: { value } }) =>
            dispatch(updateOrganization("size", value))
          }
          includeBlank
          renderInfo={
            organization.other_type || organization.type === 'fire'
              ? () => <SubTypeSizeInfo type={organization.type} other_type={organization.other_type} />
              : null
          }
        />
      )}
      {organization.type === "pos" && (
        <DropDown
          label="Zu welcher Supermarkt-Gruppe gehört Ihr Markt?"
          options={posBrands}
          value={organization.pos_brand}
          onChange={({ target: { value } }) =>
            dispatch(updateOrganization("pos_brand", value))
          }
          includeBlank
        />
      )}
      {["wedding", "business"].includes(organization.type) ? (
        <div>
          <div
            style={{
              background: "#e9e9e9",
              padding: "1rem 2rem",
              borderRadius: "15px",
              lineHeight: 1.5,
            }}
          >
            {designerPrompts[organization.type].text}
          </div>
          <div className="controls">
            <a
              className="button"
              style={{ marginTop: "2rem", textDecoration: "none" }}
              type="submit"
              href={designerPrompts[organization.type].url}
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Weiter zum Sammelerlebnis
            </a>
          </div>
        </div>
      ) : (
        <div className="controls">
          <button className="button" type="submit" disabled={invalid}>
            Weiter
          </button>
        </div>
      )}
    </form>
  );
};

const mapStateToProps = (state) => ({
  organization: state.organization,
});

export default connect(mapStateToProps)(Organization);
